import React from "react"

import { useAuth } from "@/contexts/AuthContext"

interface RoleTeamGuardProps {
  /**
   * Roles that are allowed to view the content.
   * If empty, role check is skipped.
   * Access is granted if user has ANY of these roles (OR logic with teams and permissions).
   */
  roles?: string[]

  /**
   * Teams that are allowed to view the content.
   * If empty, team check is skipped.
   * Access is granted if user has ANY of these teams (OR logic with roles and permissions).
   */
  teams?: string[]

  /**
   * Required permissions to view the content.
   * If empty, permission check is skipped.
   * Access is granted if user has ANY of these permissions (OR logic with roles and teams).
   */
  permissions?: string[]

  /**
   * Content to render if access is granted
   */
  children: React.ReactNode

  /**
   * Optional fallback content to render if access is denied
   */
  fallback?: React.ReactNode
}

/**
 * Component that conditionally renders children based on user role, team, or permissions.
 * Access is granted if the user has ANY of the specified roles, teams, or permissions (OR logic).
 */
export const RoleTeamGuard: React.FC<RoleTeamGuardProps> = ({
  roles = [],
  teams = [],
  permissions = [],
  children,
  fallback = null,
}) => {
  const { claims } = useAuth()

  // Skip checks if no restrictions are provided
  if (roles.length === 0 && teams.length === 0 && permissions.length === 0) {
    return <>{children}</>
  }

  let hasAccess = false

  // Check role if roles are specified - grant access if user has one of the required roles
  if (roles.length > 0) {
    hasAccess = hasAccess || roles.includes(claims.user_role)
  }
  // Check team if teams are specified - grant access if user has one of the required teams
  if (teams.length > 0) {
    hasAccess = hasAccess || teams.includes(claims.user_team)
  }
  // Check permissions if permissions are specified - grant access if user has at least one of the required permissions
  if (permissions.length > 0) {
    const allPermissions = [...claims.role_permissions, ...claims.team_permissions]

    // Check if user has at least one of the required permissions
    hasAccess = hasAccess || permissions.some((permission) => allPermissions.includes(permission))
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>
}

export default RoleTeamGuard
